/**
 * Curated dataset of well-known Windows Event Log IDs that sysadmins monitor.
 *
 * Sources: Microsoft "Events to Monitor" reference, Windows Security audit
 * documentation, and common operational/blue-team monitoring practice. IDs are
 * grouped by the channel they are written to (Security, System, Application).
 *
 * Severity here is a *monitoring* judgement (how much an analyst should care by
 * default), not the raw Windows "Level" field — an Audit Success logon (4624)
 * is Level=Information in the log but warrants attention when it is an unusual
 * account or logon type, hence "info" with monitoring guidance.
 */

export type Channel = 'Security' | 'System' | 'Application';
export type Severity = 'info' | 'warning' | 'critical';

export interface WinEvent {
  /** Numeric Windows Event ID. */
  id: number;
  /** Log channel the event is written to. */
  channel: Channel;
  /** Provider / source that emits the event. */
  source: string;
  /** Short human title. */
  title: string;
  /** Plain-English description of what the event means. */
  description: string;
  /** Default monitoring severity. */
  severity: Severity;
  /** Why a sysadmin would watch this event. */
  whyMonitor: string;
  /** Extra search keywords (synonyms, MITRE-ish phrases) for the search box. */
  keywords?: string[];
}

export const WIN_EVENTS: WinEvent[] = [
  // ───────────────────────────── Security channel ─────────────────────────────
  {
    id: 4624,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An account was successfully logged on',
    description:
      'A logon session was successfully created. The event records the account, the logon type (2=interactive, 3=network, 10=RemoteInteractive/RDP, etc.), the source workstation and IP.',
    severity: 'info',
    whyMonitor:
      'Baseline of who logged on where. High value when filtered for sensitive accounts, off-hours activity, RDP (type 10) from unexpected IPs, or logons to servers that should never see interactive sign-ins.',
    keywords: ['logon success', 'sign in', 'authentication', 'logon type', 'rdp logon'],
  },
  {
    id: 4625,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An account failed to log on',
    description:
      'A logon attempt failed. The Status/Sub-Status codes explain why (bad password 0xC000006A, account disabled 0xC0000072, account locked 0xC0000234, account expired, time restriction, etc.).',
    severity: 'warning',
    whyMonitor:
      'The single most important brute-force and password-spray signal. A spike of 4625 against one account or many accounts from one source is a classic attack indicator.',
    keywords: ['logon failure', 'failed login', 'bad password', 'brute force', 'password spray'],
  },
  {
    id: 4634,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An account was logged off',
    description: 'A logon session ended (logoff). Pairs with the 4624 logon of the same Logon ID.',
    severity: 'info',
    whyMonitor:
      'Used to reconstruct session duration. Mostly noise on its own; useful for forensic session timelines rather than real-time alerting.',
    keywords: ['logoff', 'session end', 'sign out'],
  },
  {
    id: 4647,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'User initiated logoff',
    description: 'A user-initiated (interactive) logoff was requested, distinct from the system-generated 4634.',
    severity: 'info',
    whyMonitor: 'Forensic session reconstruction; helps distinguish a real user sign-out from a session teardown.',
    keywords: ['logoff', 'user logoff'],
  },
  {
    id: 4648,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A logon was attempted using explicit credentials',
    description:
      'A process used explicit credentials (e.g., runas, scheduled task, or a service connecting as another user) rather than the logged-on user’s context.',
    severity: 'warning',
    whyMonitor:
      'Frequently logged during lateral movement and credential theft (e.g., "runas /user", PsExec with alternate creds). Watch for one host explicitly authenticating as many accounts.',
    keywords: ['explicit credentials', 'runas', 'lateral movement', 'alternate credentials'],
  },
  {
    id: 4672,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'Special privileges assigned to new logon',
    description:
      'A logon was granted high-power privileges (SeDebugPrivilege, SeTcbPrivilege, SeBackupPrivilege, etc.) — effectively an administrative/privileged sign-on.',
    severity: 'warning',
    whyMonitor:
      'Tracks every privileged logon. Pair with 4624 to know when an admin-equivalent session starts; unexpected accounts here can indicate privilege escalation.',
    keywords: ['admin logon', 'privileged logon', 'sedebugprivilege', 'special privileges'],
  },
  {
    id: 4688,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A new process has been created',
    description:
      'Process-creation auditing. Records the new process name, PID, parent process, and (when command-line auditing is enabled) the full command line.',
    severity: 'info',
    whyMonitor:
      'Cornerstone of endpoint detection. With command-line logging on, this catches living-off-the-land abuse (powershell -enc, mshta, rundll32, wmic, certutil downloads).',
    keywords: ['process creation', 'command line', 'lolbin', 'powershell', 'sysmon alternative'],
  },
  {
    id: 4689,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A process has exited',
    description: 'Records the termination of a process and its exit status. Pairs with 4688.',
    severity: 'info',
    whyMonitor: 'Process lifetime analysis; mostly forensic. Useful to confirm short-lived tooling ran and exited.',
    keywords: ['process exit', 'process terminated'],
  },
  {
    id: 4697,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A service was installed in the system',
    description:
      'A new Windows service was installed (Security-channel equivalent of System 7045). Records service name, image path, account, and start type.',
    severity: 'warning',
    whyMonitor:
      'New services are a common persistence and lateral-movement mechanism (e.g., PsExec creates a service). Alert on services with suspicious image paths or that run as LocalSystem.',
    keywords: ['service installed', 'persistence', 'psexec', 'new service'],
  },
  {
    id: 4698,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A scheduled task was created',
    description: 'A new scheduled task was registered. Includes the task name and the XML definition of the action.',
    severity: 'warning',
    whyMonitor:
      'Scheduled tasks are a top-tier persistence technique (MITRE T1053). Watch for tasks created by non-admin tooling or running scripts/encoded commands.',
    keywords: ['scheduled task', 'persistence', 'schtasks', 't1053'],
  },
  {
    id: 4699,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A scheduled task was deleted',
    description: 'An existing scheduled task was removed.',
    severity: 'info',
    whyMonitor: 'Attackers sometimes delete tasks to clean up. Correlate with 4698 to spot create/delete churn.',
    keywords: ['scheduled task deleted', 'anti-forensics'],
  },
  {
    id: 4700,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A scheduled task was enabled',
    description: 'A previously disabled scheduled task was enabled.',
    severity: 'info',
    whyMonitor: 'Re-enabling a dormant task can be a persistence trigger; correlate with task creation events.',
    keywords: ['scheduled task enabled'],
  },
  {
    id: 4702,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A scheduled task was updated',
    description: 'An existing scheduled task definition was modified.',
    severity: 'info',
    whyMonitor: 'Modifying a legitimate task to run malicious actions is a stealthy persistence trick.',
    keywords: ['scheduled task updated', 'persistence'],
  },
  {
    id: 4719,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'System audit policy was changed',
    description: 'The local audit policy (what gets logged) was modified.',
    severity: 'critical',
    whyMonitor:
      'Disabling auditing is a classic anti-forensics / defense-evasion step. Any unexpected change to audit policy should be alerted on immediately.',
    keywords: ['audit policy changed', 'defense evasion', 'disable logging', 'anti-forensics'],
  },
  {
    id: 4720,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was created',
    description: 'A new local or domain user account was created. Records the new account name and the creator.',
    severity: 'warning',
    whyMonitor:
      'Unauthorized account creation is a persistence/backdoor signal. Alert on accounts created outside change windows or by unexpected operators.',
    keywords: ['user created', 'account created', 'new user', 'backdoor account', 'persistence'],
  },
  {
    id: 4722,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was enabled',
    description: 'A disabled user account was re-enabled.',
    severity: 'warning',
    whyMonitor: 'Re-enabling a dormant/disabled account (especially a former employee or service account) is a common backdoor.',
    keywords: ['account enabled', 'reactivated account'],
  },
  {
    id: 4723,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An attempt was made to change an account’s password',
    description: 'A user attempted to change their own password (knowing the old one).',
    severity: 'info',
    whyMonitor: 'Baseline; correlate with 4724 (admin reset) to distinguish self-service from administrative resets.',
    keywords: ['password change', 'self-service password'],
  },
  {
    id: 4724,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An attempt was made to reset an account’s password',
    description: 'A privileged account reset another account’s password (no knowledge of the old password required).',
    severity: 'warning',
    whyMonitor:
      'Admin password resets on sensitive/privileged accounts can be account takeover. Alert on resets of admin, service, or executive accounts.',
    keywords: ['password reset', 'admin reset', 'account takeover'],
  },
  {
    id: 4725,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was disabled',
    description: 'A user account was disabled.',
    severity: 'info',
    whyMonitor: 'Normal during offboarding; unexpected disables can be a denial-of-service or sabotage indicator.',
    keywords: ['account disabled', 'offboarding'],
  },
  {
    id: 4726,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was deleted',
    description: 'A user account was deleted from the local SAM or directory.',
    severity: 'warning',
    whyMonitor:
      'Account deletion can be sabotage or anti-forensics (removing an attacker-created account). Alert on deletions outside HR offboarding processes.',
    keywords: ['account deleted', 'user deleted', 'anti-forensics'],
  },
  {
    id: 4728,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A member was added to a security-enabled global group',
    description: 'An account was added to a global security group (e.g., a domain group).',
    severity: 'warning',
    whyMonitor: 'Group membership changes drive access. Watch additions to privileged groups closely.',
    keywords: ['group membership', 'added to group', 'global group'],
  },
  {
    id: 4732,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A member was added to a security-enabled local group',
    description:
      'An account was added to a local security group — including the local Administrators group on a server or workstation.',
    severity: 'critical',
    whyMonitor:
      'Addition to local Administrators is a direct privilege-escalation/persistence signal. Alert on any change to Administrators membership.',
    keywords: ['local administrators', 'added to admins', 'privilege escalation', 'group membership'],
  },
  {
    id: 4756,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A member was added to a security-enabled universal group',
    description: 'An account was added to a universal security group (e.g., Enterprise Admins is a universal group).',
    severity: 'critical',
    whyMonitor:
      'Universal groups like Enterprise Admins / Schema Admins are crown-jewel groups. Any membership change deserves an immediate alert.',
    keywords: ['enterprise admins', 'universal group', 'privilege escalation'],
  },
  {
    id: 4738,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was changed',
    description: 'Attributes of a user account were modified (UAC flags, password-never-expires, SPNs, etc.).',
    severity: 'warning',
    whyMonitor:
      'Subtle account changes (e.g., setting "password never expires", disabling pre-auth for Kerberoasting/AS-REP roasting) are stealthy attacker prep.',
    keywords: ['account changed', 'uac flags', 'asrep roasting', 'spn'],
  },
  {
    id: 4740,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was locked out',
    description: 'An account was locked out after exceeding the bad-password threshold.',
    severity: 'warning',
    whyMonitor:
      'Lockout storms indicate brute force, a stale cached credential, or a misconfigured service. The Caller Computer Name field points at the source.',
    keywords: ['account lockout', 'locked out', 'bad password', 'brute force'],
  },
  {
    id: 4767,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user account was unlocked',
    description: 'A locked-out account was unlocked by an administrator or automatically.',
    severity: 'info',
    whyMonitor: 'Correlate with 4740 to measure lockout churn and helpdesk load.',
    keywords: ['account unlocked'],
  },
  {
    id: 4768,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A Kerberos authentication ticket (TGT) was requested',
    description:
      'A domain controller issued (or denied) a Kerberos TGT — i.e., a user authenticated to the KDC. Result/failure codes describe denials.',
    severity: 'info',
    whyMonitor:
      'DC-side authentication baseline. Failure codes here surface bad passwords at the KDC and AS-REP roasting attempts (pre-auth not required).',
    keywords: ['kerberos', 'tgt', 'kdc', 'as-rep roasting', 'authentication'],
  },
  {
    id: 4769,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A Kerberos service ticket (TGS) was requested',
    description: 'A service ticket was requested for a specific service principal name (SPN).',
    severity: 'warning',
    whyMonitor:
      'Kerberoasting detection: a flood of TGS requests for many SPNs (especially with RC4 encryption type 0x17) from one account is the classic signature.',
    keywords: ['kerberos', 'tgs', 'service ticket', 'kerberoasting', 'spn', 'rc4'],
  },
  {
    id: 4771,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'Kerberos pre-authentication failed',
    description: 'A Kerberos pre-authentication attempt failed at the DC — the Kerberos equivalent of a bad password.',
    severity: 'warning',
    whyMonitor:
      'Primary signal for password guessing against AD (failure code 0x18 = bad password). Spikes indicate brute force or spraying at the domain level.',
    keywords: ['kerberos pre-auth', 'failed', 'bad password', 'brute force', '0x18'],
  },
  {
    id: 4776,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'The DC attempted to validate credentials (NTLM)',
    description: 'An NTLM credential validation occurred on a domain controller; error codes indicate success or failure.',
    severity: 'warning',
    whyMonitor:
      'NTLM auth failures (error 0xC000006A) reveal brute force against legacy/NTLM endpoints that don’t generate Kerberos events.',
    keywords: ['ntlm', 'credential validation', 'legacy auth', 'brute force'],
  },
  {
    id: 4781,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'The name of an account was changed',
    description: 'A user or computer account was renamed.',
    severity: 'info',
    whyMonitor: 'Renaming accounts (e.g., disguising a backdoor as a service account) is an occasional evasion tactic.',
    keywords: ['account renamed', 'evasion'],
  },
  {
    id: 4798,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A user’s local group membership was enumerated',
    description: 'A process queried the local group membership of a user.',
    severity: 'info',
    whyMonitor:
      'Mass enumeration (e.g., by recon tools like BloodHound/SharpHound) shows up as bursts of 4798/4799 across hosts.',
    keywords: ['enumeration', 'recon', 'bloodhound', 'discovery'],
  },
  {
    id: 4799,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A security-enabled local group membership was enumerated',
    description: 'A process enumerated the members of a local security group (e.g., local Administrators).',
    severity: 'info',
    whyMonitor: 'Enumerating local Administrators across many hosts is a hallmark of attacker reconnaissance.',
    keywords: ['enumeration', 'recon', 'local administrators', 'bloodhound'],
  },
  {
    id: 1102,
    channel: 'Security',
    source: 'Microsoft-Windows-Eventlog',
    title: 'The audit log was cleared',
    description: 'The Security event log was cleared. Records the account that performed the clear.',
    severity: 'critical',
    whyMonitor:
      'Clearing the Security log is a strong anti-forensics / cover-tracks indicator and is almost never legitimate in production. Alert immediately.',
    keywords: ['log cleared', 'audit log cleared', 'anti-forensics', 'defense evasion', 'cover tracks'],
  },
  {
    id: 4616,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'The system time was changed',
    description: 'The system clock was changed, recording the previous and new time and the responsible process/account.',
    severity: 'warning',
    whyMonitor:
      'Time tampering breaks log correlation and Kerberos (5-minute skew). Unexpected changes can be anti-forensics or a sign of malware.',
    keywords: ['time changed', 'clock', 'kerberos skew', 'anti-forensics'],
  },
  {
    id: 1100,
    channel: 'Security',
    source: 'Microsoft-Windows-Eventlog',
    title: 'The event logging service has shut down',
    description: 'The Windows Event Log service stopped — no further Security events will be recorded until it restarts.',
    severity: 'warning',
    whyMonitor: 'A gap in logging is itself suspicious. Pairs with 1102 as a cover-tracks indicator.',
    keywords: ['event log service stopped', 'logging gap'],
  },
  {
    id: 4946,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A Windows Firewall exception rule was added',
    description: 'A rule was added to the Windows Defender Firewall exception list.',
    severity: 'warning',
    whyMonitor: 'Attackers add firewall rules to open C2 or lateral-movement ports. Watch for rules added outside change control.',
    keywords: ['firewall rule added', 'exception', 'c2', 'lateral movement'],
  },
  {
    id: 4950,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A Windows Firewall setting was changed',
    description: 'A Windows Defender Firewall setting (e.g., profile state) was modified.',
    severity: 'warning',
    whyMonitor: 'Disabling the firewall is a common defense-evasion step. Alert on firewall being turned off.',
    keywords: ['firewall disabled', 'firewall setting', 'defense evasion'],
  },
  {
    id: 5140,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A network share object was accessed',
    description: 'A network share (including administrative shares like C$ / ADMIN$) was accessed.',
    severity: 'info',
    whyMonitor:
      'Access to ADMIN$ / C$ from unusual hosts is a lateral-movement signal (PsExec, SMB tooling). High value when filtered to admin shares.',
    keywords: ['share access', 'admin$', 'c$', 'smb', 'lateral movement'],
  },
  {
    id: 5145,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A network share object was checked for access (detailed)',
    description: 'Detailed file-share access check, including the relative target name (file/path within the share).',
    severity: 'info',
    whyMonitor:
      'Detailed share auditing surfaces tools writing payloads to ADMIN$ (e.g., PsExec drops a service binary) and mass file access.',
    keywords: ['detailed share access', 'admin$', 'psexec', 'file share'],
  },
  {
    id: 4657,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'A registry value was modified',
    description: 'A monitored registry value was changed (requires a SACL on the key).',
    severity: 'warning',
    whyMonitor:
      'Registry persistence (Run keys, services) and configuration tampering show here when audit SACLs are configured on sensitive keys.',
    keywords: ['registry modified', 'run key', 'persistence', 'sacl'],
  },
  {
    id: 4663,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'An attempt was made to access an object',
    description: 'Object-access auditing fired for a file, registry key, or other securable object with a configured SACL.',
    severity: 'info',
    whyMonitor: 'File-integrity / sensitive-data access monitoring. Filter to crown-jewel paths to keep volume sane.',
    keywords: ['object access', 'file access', 'sacl', 'file integrity'],
  },
  {
    id: 4670,
    channel: 'Security',
    source: 'Microsoft-Windows-Security-Auditing',
    title: 'Permissions on an object were changed',
    description: 'The DACL (permissions) on an object was modified.',
    severity: 'warning',
    whyMonitor: 'Permission changes on sensitive files/keys can be privilege escalation or backdooring of access.',
    keywords: ['permissions changed', 'dacl', 'privilege escalation'],
  },

  // ───────────────────────────── System channel ──────────────────────────────
  {
    id: 41,
    channel: 'System',
    source: 'Microsoft-Windows-Kernel-Power',
    title: 'Kernel-Power — system rebooted without cleanly shutting down',
    description:
      'The system was restarted without first shutting down cleanly (power loss, hard reset, bugcheck/BSOD without dump, or a hard hang).',
    severity: 'critical',
    whyMonitor:
      'A leading indicator of power problems, hardware faults, overheating, or crashes. Recurring 41s on a server demand investigation.',
    keywords: ['kernel power', 'unexpected reboot', 'power loss', 'bsod', 'crash', 'dirty shutdown'],
  },
  {
    id: 1074,
    channel: 'System',
    source: 'User32 / Microsoft-Windows-Winlogon',
    title: 'The system has been shut down or restarted (initiated)',
    description:
      'A process or user initiated a shutdown or restart. Records the process, the user, the reason code, and whether it was planned.',
    severity: 'info',
    whyMonitor:
      'Distinguishes planned reboots (patching) from unexpected ones, and shows WHO/what triggered a server restart.',
    keywords: ['shutdown initiated', 'restart', 'reboot reason', 'planned shutdown'],
  },
  {
    id: 1076,
    channel: 'System',
    source: 'Microsoft-Windows-Winlogon',
    title: 'Reason supplied for the previous unexpected shutdown',
    description: 'After an unexpected shutdown, the first user to log on supplied (or was prompted for) a reason.',
    severity: 'info',
    whyMonitor: 'Correlates with 6008/41 to attribute unplanned downtime.',
    keywords: ['shutdown reason', 'unexpected shutdown'],
  },
  {
    id: 6005,
    channel: 'System',
    source: 'EventLog',
    title: 'The Event log service was started',
    description: 'The Event Log service started — effectively marks a boot ("the computer started up").',
    severity: 'info',
    whyMonitor: 'Boot marker. Pair 6005 (start) with 6006 (clean stop) to detect dirty shutdowns and measure uptime.',
    keywords: ['event log started', 'boot', 'startup', 'uptime'],
  },
  {
    id: 6006,
    channel: 'System',
    source: 'EventLog',
    title: 'The Event log service was stopped',
    description: 'The Event Log service stopped cleanly — marks a graceful shutdown.',
    severity: 'info',
    whyMonitor: 'A missing 6006 before a 6005 means the box did not shut down cleanly (correlate with 6008/41).',
    keywords: ['event log stopped', 'clean shutdown'],
  },
  {
    id: 6008,
    channel: 'System',
    source: 'EventLog',
    title: 'The previous system shutdown was unexpected',
    description: 'On boot, the system detected that the previous shutdown was unexpected (no clean 6006).',
    severity: 'critical',
    whyMonitor:
      'Direct evidence of an unplanned outage / crash. Recurring 6008 is a strong reliability red flag for servers.',
    keywords: ['unexpected shutdown', 'dirty shutdown', 'crash', 'outage'],
  },
  {
    id: 6013,
    channel: 'System',
    source: 'EventLog',
    title: 'System uptime report',
    description: 'Periodic event reporting how long the system has been up (uptime in seconds).',
    severity: 'info',
    whyMonitor: 'Sanity-check uptime and detect reboots you did not expect (uptime resets unexpectedly).',
    keywords: ['uptime', 'system uptime'],
  },
  {
    id: 7000,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service failed to start',
    description: 'The Service Control Manager could not start a service (bad path, missing dependency, permissions, etc.).',
    severity: 'warning',
    whyMonitor: 'Failed critical services (SQL, IIS, backup agents) cause outages. Alert on start failures of monitored services.',
    keywords: ['service failed to start', 'scm', 'service error'],
  },
  {
    id: 7009,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'Timeout waiting for a service to connect',
    description: 'A service did not respond to the start request within the timeout (default 30s).',
    severity: 'warning',
    whyMonitor: 'Slow/hung service starts often precede crashes or indicate resource starvation at boot.',
    keywords: ['service timeout', 'service hung', 'scm'],
  },
  {
    id: 7011,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'Timeout waiting for a service transaction response',
    description: 'A service control request (start/stop/etc.) timed out, often a symptom of a deadlocked or overloaded host.',
    severity: 'warning',
    whyMonitor: 'Clusters of 7011 typically accompany resource exhaustion, failing disks, or driver problems.',
    keywords: ['service transaction timeout', 'deadlock', 'resource exhaustion'],
  },
  {
    id: 7022,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service hung on starting',
    description: 'A service started but then hung.',
    severity: 'warning',
    whyMonitor: 'Hung services degrade dependent applications; correlate with 7009/7011.',
    keywords: ['service hung', 'hang on start'],
  },
  {
    id: 7023,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service terminated with an error',
    description: 'A service stopped and reported a specific Win32/HRESULT error code.',
    severity: 'warning',
    whyMonitor: 'The error code points at the root cause (e.g., access denied, dependency failure). Alert for critical services.',
    keywords: ['service terminated', 'service error code'],
  },
  {
    id: 7031,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service terminated unexpectedly (with recovery action)',
    description:
      'A service crashed unexpectedly N times; the SCM recorded the configured recovery action (e.g., restart).',
    severity: 'warning',
    whyMonitor:
      'Repeated 7031 for one service means a crash loop. Even with auto-restart it signals instability and possible memory leaks.',
    keywords: ['service crashed', 'unexpected termination', 'crash loop', 'recovery action'],
  },
  {
    id: 7034,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service terminated unexpectedly',
    description: 'A service stopped unexpectedly (crashed) a given number of times.',
    severity: 'warning',
    whyMonitor:
      'Core service-crash signal. A monitored service crashing should page someone; track the count to spot crash loops.',
    keywords: ['service crashed', 'service terminated unexpectedly', 'crash'],
  },
  {
    id: 7036,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A service entered the running/stopped state',
    description: 'Routine service state-transition message (started or stopped).',
    severity: 'info',
    whyMonitor:
      'High-volume and mostly noise, but useful to confirm a specific service stopped/started, or to detect a service being stopped by an attacker (e.g., AV/EDR).',
    keywords: ['service state', 'service stopped', 'service started', 'av stopped'],
  },
  {
    id: 7040,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'The start type of a service was changed',
    description: 'A service’s start type changed (e.g., Automatic → Disabled).',
    severity: 'warning',
    whyMonitor:
      'Disabling defensive services (Windows Defender, Event Log, firewall) by changing start type to Disabled is a defense-evasion technique.',
    keywords: ['service start type changed', 'disabled service', 'defense evasion'],
  },
  {
    id: 7045,
    channel: 'System',
    source: 'Service Control Manager',
    title: 'A new service was installed',
    description: 'A new service was installed in the system. Records the service name, image path, account, and start type.',
    severity: 'warning',
    whyMonitor:
      'Top persistence/lateral-movement signal (PsExec, malware droppers, ransomware). Alert on services with random names or paths in temp/user dirs.',
    keywords: ['service installed', 'new service', 'persistence', 'psexec', 'ransomware'],
  },
  {
    id: 1001,
    channel: 'System',
    source: 'Microsoft-Windows-WER-SystemErrorReporting / BugCheck',
    title: 'Bugcheck (Blue Screen of Death) reported',
    description:
      'Windows Error Reporting recorded a bugcheck (BSOD) with the stop code and parameters from the previous crash.',
    severity: 'critical',
    whyMonitor:
      'A BSOD is a hard crash. The stop code (e.g., 0x000000D1, 0x0000007E) directs driver/hardware troubleshooting. Recurrent BSODs need urgent attention.',
    keywords: ['bsod', 'bugcheck', 'blue screen', 'stop code', 'crash dump'],
  },
  {
    id: 7,
    channel: 'System',
    source: 'Disk',
    title: 'The device has a bad block',
    description: 'The disk driver reported a bad block on a physical disk.',
    severity: 'critical',
    whyMonitor: 'Early warning of a failing drive. Combined with SMART data, predicts disk failure before data loss.',
    keywords: ['disk bad block', 'disk error', 'failing drive', 'storage'],
  },
  {
    id: 51,
    channel: 'System',
    source: 'Disk',
    title: 'An error was detected on the device during a paging operation',
    description: 'A read/write error occurred on a disk during a paging operation.',
    severity: 'warning',
    whyMonitor: 'Often the first sign of a degrading disk or flaky storage path (cabling, controller). Watch for clusters.',
    keywords: ['disk paging error', 'storage error', 'failing disk'],
  },
  {
    id: 153,
    channel: 'System',
    source: 'disk / storahci',
    title: 'The IO operation was retried',
    description: 'A disk I/O operation failed and was retried by the storage stack.',
    severity: 'warning',
    whyMonitor: 'Frequent I/O retries indicate a struggling disk, SAN path issues, or an overloaded storage subsystem.',
    keywords: ['io retry', 'disk retry', 'storage latency', 'san'],
  },
  {
    id: 129,
    channel: 'System',
    source: 'storahci / storport',
    title: 'Reset to device issued (storage controller)',
    description: 'The storage controller issued a reset to a device because it stopped responding.',
    severity: 'warning',
    whyMonitor: 'Controller resets point at failing drives, firmware bugs, or saturated storage; correlate with 153/51.',
    keywords: ['storage reset', 'controller reset', 'storahci', 'disk timeout'],
  },
  {
    id: 55,
    channel: 'System',
    source: 'Ntfs',
    title: 'The file system structure on the volume is corrupt',
    description: 'NTFS detected corruption on a volume and recommends running chkdsk.',
    severity: 'critical',
    whyMonitor: 'Filesystem corruption risks data loss and downtime. Alert immediately and schedule a chkdsk/health check.',
    keywords: ['ntfs corruption', 'filesystem corrupt', 'chkdsk', 'volume error'],
  },
  {
    id: 1014,
    channel: 'System',
    source: 'Microsoft-Windows-DNS-Client',
    title: 'Name resolution for a name timed out',
    description: 'The DNS client failed to resolve a name within the timeout after querying all configured servers.',
    severity: 'warning',
    whyMonitor: 'Repeated DNS timeouts break apps and AD. Often points at a DNS server outage or network path problem.',
    keywords: ['dns timeout', 'name resolution', 'dns failure'],
  },
  {
    id: 36888,
    channel: 'System',
    source: 'Schannel',
    title: 'A fatal TLS/SSL alert was generated (Schannel)',
    description: 'Schannel generated a fatal alert during a TLS handshake (the local side reported the error).',
    severity: 'warning',
    whyMonitor: 'Bursts of Schannel errors flag TLS/cipher mismatches, expiring certs, or protocol-deprecation breakage.',
    keywords: ['schannel', 'tls error', 'ssl handshake', 'cipher mismatch'],
  },

  // ───────────────────────────── Application channel ─────────────────────────
  {
    id: 1000,
    channel: 'Application',
    source: 'Application Error',
    title: 'Application error (crash)',
    description:
      'A user-mode application crashed. Records the faulting application name/version, the faulting module, and the exception/offset.',
    severity: 'warning',
    whyMonitor:
      'Application crash signal. The faulting module pinpoints the culprit DLL; clusters indicate a bad deploy, driver, or library update.',
    keywords: ['application error', 'app crash', 'faulting module', 'crash'],
  },
  {
    id: 1002,
    channel: 'Application',
    source: 'Application Hang',
    title: 'Application hang (stopped responding)',
    description: 'An application stopped responding (hung) and was reported by the hang-detection mechanism.',
    severity: 'warning',
    whyMonitor: 'Hangs frustrate users and can precede crashes; correlate with resource exhaustion on the host.',
    keywords: ['application hang', 'not responding', 'hang'],
  },
  {
    id: 1026,
    channel: 'Application',
    source: '.NET Runtime',
    title: '.NET Runtime unhandled exception',
    description:
      'The .NET CLR reported an unhandled exception that terminated a managed application. Includes the exception type and stack.',
    severity: 'warning',
    whyMonitor:
      'Primary crash signal for .NET line-of-business apps and services. The stack trace drives the fix; spikes mean a regression.',
    keywords: ['.net runtime', 'clr', 'unhandled exception', 'managed crash', 'stack trace'],
  },
  {
    id: 1023,
    channel: 'Application',
    source: '.NET Runtime',
    title: '.NET Runtime fatal execution engine error',
    description: 'The .NET execution engine hit a fatal error.',
    severity: 'critical',
    whyMonitor: 'Engine-level .NET failures are severe and often point at corrupted runtime installs or bad native interop.',
    keywords: ['.net fatal', 'execution engine', 'clr crash'],
  },
  {
    id: 1309,
    channel: 'Application',
    source: 'ASP.NET',
    title: 'ASP.NET unhandled exception (event 1309)',
    description: 'ASP.NET recorded an unhandled exception in a web application, including request URL and stack trace.',
    severity: 'warning',
    whyMonitor: 'Web-app error tracking without an APM. Spikes correlate with bad deploys or downstream dependency failures.',
    keywords: ['asp.net', 'web application error', 'unhandled exception', 'iis app error'],
  },
  {
    id: 11707,
    channel: 'Application',
    source: 'MsiInstaller',
    title: 'Product installed successfully (MSI)',
    description: 'Windows Installer reported that a product (MSI) installed successfully.',
    severity: 'info',
    whyMonitor: 'Software-inventory and change tracking; detect unexpected software installs on servers.',
    keywords: ['msi install', 'software installed', 'msiinstaller', 'inventory'],
  },
  {
    id: 11708,
    channel: 'Application',
    source: 'MsiInstaller',
    title: 'Product install operation failed (MSI)',
    description: 'A Windows Installer operation failed.',
    severity: 'warning',
    whyMonitor: 'Failed installs/patches leave software in an inconsistent state. Alert when deploying critical updates.',
    keywords: ['msi install failed', 'installer error', 'patch failed'],
  },
  {
    id: 11724,
    channel: 'Application',
    source: 'MsiInstaller',
    title: 'Product removed successfully (MSI)',
    description: 'Windows Installer reported that a product was uninstalled successfully.',
    severity: 'info',
    whyMonitor: 'Detect unexpected removal of agents (AV/EDR/backup) — uninstalling security tooling is an attacker tactic.',
    keywords: ['msi uninstall', 'software removed', 'agent removed'],
  },
  {
    id: 1530,
    channel: 'Application',
    source: 'Microsoft-Windows-User Profiles Service',
    title: 'Windows detected your registry file is still in use (profile)',
    description: 'A user profile’s registry hive was still in use at logoff, preventing a clean profile unload.',
    severity: 'info',
    whyMonitor: 'Profile-unload problems cause logon/logoff slowness and roaming-profile corruption on terminal/RDS servers.',
    keywords: ['user profile', 'registry in use', 'profile unload', 'rds'],
  },
  {
    id: 17,
    channel: 'Application',
    source: 'Microsoft-Windows-PerfNet',
    title: 'Unable to read performance data',
    description: 'A performance-counter provider could not return data, often after a bad install or corrupted counters.',
    severity: 'info',
    whyMonitor: 'Broken perf counters blind your monitoring; fix with lodctr /R. Low urgency but worth cleaning up.',
    keywords: ['perf counters', 'performance data', 'lodctr'],
  },
  {
    id: 9,
    channel: 'System',
    source: 'aacraid / storage driver',
    title: 'The device did not respond within the timeout period',
    description: 'A storage device failed to respond to a command within the timeout.',
    severity: 'warning',
    whyMonitor: 'Storage timeouts precede data-availability incidents; correlate with 153/129 and SAN/array health.',
    keywords: ['device timeout', 'storage timeout', 'disk not responding'],
  },
  {
    id: 1058,
    channel: 'System',
    source: 'Microsoft-Windows-GroupPolicy',
    title: 'Group Policy processing failed (could not read GPO)',
    description: 'The Group Policy engine could not read a GPO file from SYSVOL (often a DFS/replication or permissions problem).',
    severity: 'warning',
    whyMonitor: 'GPO failures mean policy (including security settings) is not applying. Widespread 1058s indicate AD/SYSVOL trouble.',
    keywords: ['group policy failed', 'gpo', 'sysvol', '1058'],
  },
  {
    id: 5722,
    channel: 'System',
    source: 'NETLOGON',
    title: 'The session setup from the computer failed (secure channel)',
    description: 'A machine’s secure channel to the domain failed to authenticate (broken computer-account password/trust).',
    severity: 'warning',
    whyMonitor: 'Broken machine trust locks a server out of the domain. Alert so you can reset the secure channel before logons fail.',
    keywords: ['secure channel', 'netlogon', 'machine trust', 'broken trust'],
  },
  {
    id: 5719,
    channel: 'System',
    source: 'NETLOGON',
    title: 'No domain controller available for the domain',
    description: 'The Netlogon service could not locate a domain controller for the machine’s domain.',
    severity: 'warning',
    whyMonitor: 'Indicates DC reachability/DNS problems; users may fail to authenticate. Correlate with network and DNS events.',
    keywords: ['no domain controller', 'netlogon', 'dc unavailable', 'authentication outage'],
  },
];

/**
 * Sorted, de-duplicated list consumed by the UI. Dedupe is keyed on
 * channel:id (the same numeric ID can legitimately appear in different
 * channels) so a ?id= deep link plus channel resolves to a single entry.
 */
export const EVENTS: WinEvent[] = (() => {
  const seen = new Set<string>();
  const out: WinEvent[] = [];
  for (const e of WIN_EVENTS) {
    const key = `${e.channel}:${e.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out.sort((a, b) => a.id - b.id);
})();

export const CHANNELS: Channel[] = ['Security', 'System', 'Application'];
export const SEVERITIES: Severity[] = ['info', 'warning', 'critical'];

export const SEVERITY_META: Record<Severity, { label: string; badge: string; dot: string }> = {
  info: {
    label: 'Info',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
  },
  warning: {
    label: 'Warning',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  critical: {
    label: 'Critical',
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
  },
};
